// :)
/**
 * CLI API client for project and violation endpoints.
 *
 * Calls the Synkro API (Hono/Cloudflare Workers) for project management, violations, and policy upsert.
 * SYNKRO_CRUD_URL points to the API server (defaults to http://localhost:8788/api).
 */

import { getAccessToken, ensureValidToken } from '../auth/index.js';

let API_URL = 'https://api.synkro.sh/api';

export function setApiBaseUrl(url: string): void {
  API_URL = url;
}

// Types

export interface Project {
  id: string;
  slug: string;
  name: string;
  provider: string | null;
  is_active: boolean;
  api_key_count: number;
  created_at: string | null;
  repos?: Array<{ id: string; github_repo_id: number | null; full_name: string }>;
}

export interface Violation {
  id: string | null;
  run_id: string | null;
  score: number | null;
  severity: string | null;
  issues: string[];
  rules_violated: string[];
  messages: Array<{ role: string; content: string }>;
  model: string | null;
  comment: string | null;
  created_at: string | null;
}

export interface ViolationsResponse {
  violations: Violation[];
  total: number;
  project_id: string;
  project_slug: string | null;
  policy_text: string | null;
  rules: Array<Record<string, unknown>>;
}

export interface GetViolationsOptions {
  limit?: number;
  offset?: number;
  severity?: string;
}

export interface PolicyUpsertResponse {
  ok: boolean;
  policy_id: string;
}

// API helper

async function callApi<T>(
  method: string,
  endpoint: string,
  body?: Record<string, unknown>
): Promise<T> {
  if (!API_URL) {
    throw new Error('API URL not configured. Run `synkro install` first.');
  }

  const url = `${API_URL}${endpoint}`;
  await ensureValidToken();
  const accessToken = getAccessToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `API error: ${response.status}`);
  }

  return response.json();
}

export interface CreateProjectResponse {
  id: string;
  slug: string;
  name: string;
  provider: string | null;
  is_active: boolean;
  created_at: string | null;
}

export interface CreateApiKeyResponse {
  id: string;
  key: string;
  key_prefix: string;
  name: string;
  project_id: string;
  created_at: string | null;
}

// Project API functions

/**
 * Create a new project.
 */
export async function createProject(
  name: string,
  repos?: Array<{ github_repo_id?: number; full_name: string; default_branch?: string; private?: boolean }>,
): Promise<CreateProjectResponse> {
  const body: Record<string, unknown> = { name };
  if (repos && repos.length > 0) body.repos = repos;
  return callApi<CreateProjectResponse>('POST', '/projects', body);
}

/**
 * Create an API key for a project. Returns plaintext key (shown once).
 */
export async function createApiKey(
  projectId: string,
  name?: string
): Promise<CreateApiKeyResponse> {
  return callApi<CreateApiKeyResponse>('POST', '/api-keys', {
    project_id: projectId,
    name: name || 'CLI Key',
  });
}

/**
 * List all projects for the authenticated user.
 */
export async function listProjects(): Promise<Project[]> {
  return callApi<Project[]>('GET', '/projects');
}

/**
 * Get violations for a project with optional filtering.
 * Also returns the active policy text and rules.
 */
export async function getViolations(
  projectId: string,
  options: GetViolationsOptions = {}
): Promise<ViolationsResponse> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.offset) params.set('offset', String(options.offset));
  if (options.severity) params.set('severity', options.severity);

  const qs = params.toString();
  const endpoint = `/projects/${projectId}/violations${qs ? `?${qs}` : ''}`;
  return callApi<ViolationsResponse>('GET', endpoint);
}

/**
 * Upsert a policy for a project (deploy rules to gateway).
 */
export async function upsertPolicy(
  projectId: string,
  policyText: string,
  rules: Array<Record<string, unknown>>,
  ruleCount: number
): Promise<PolicyUpsertResponse> {
  return callApi<PolicyUpsertResponse>('POST', `/projects/${projectId}/policies`, {
    policy_text: policyText,
    rules,
    rule_count: ruleCount,
  });
}

/**
 * Update a project's configuration.
 */
export async function updateProject(
  projectId: string,
  updates: {
    name?: string;
    provider?: string;
    langsmith_project?: string;
    is_active?: boolean;
  }
): Promise<{ ok: boolean }> {
  return callApi<{ ok: boolean }>('PATCH', `/projects/${projectId}`, updates as Record<string, unknown>);
}

/**
 * Unlink a repo from a project.
 */
export async function unlinkRepo(
  projectId: string,
  repoId: string,
): Promise<{ ok: boolean }> {
  return callApi<{ ok: boolean }>('DELETE', `/projects/${projectId}/repos/${repoId}`);
}

/**
 * Resolve a project by name or slug (fuzzy match against user's projects).
 * Returns the first matching project or null.
 */
export async function resolveProject(nameOrSlug: string): Promise<Project | null> {
  const projects = await listProjects();
  const query = nameOrSlug.toLowerCase();

  // Exact slug match
  const exactSlug = projects.find(p => p.slug === nameOrSlug);
  if (exactSlug) return exactSlug;

  // Exact name match (case-insensitive)
  const exactName = projects.find(p => p.name.toLowerCase() === query);
  if (exactName) return exactName;

  // Partial name match
  const partial = projects.find(p => p.name.toLowerCase().includes(query));
  if (partial) return partial;

  // Partial slug match
  const partialSlug = projects.find(p => p.slug.includes(query));
  if (partialSlug) return partialSlug;

  return null;
}
